import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ProgramConfigManager.css';
import { getApiBase, normalizeFetchError } from '../utils/apiBase';

type ProgramData = { [key: string]: any };

interface ProgramConfigResponse {
  data?: ProgramData;
  updated_at?: string | null;
  updated_by?: string | null;
}

interface ApartmentConfigItem {
  apartment_name: string;
  apartment_id?: string;
  apartment_address?: string;
  entrance_password?: string;
}

interface ConfigRevision {
  id: number;
  note?: string;
  created_by?: string;
  created_at?: string;
  data?: ProgramData;
}

interface JusoSearchResult {
  road_addr: string;
  jibun_addr?: string;
  zip_no?: string;
  building_name?: string;
}

interface RecipientDraft {
  delivery_profile_id: string;
  profile_version: string;
  dong: string;
  ho: string;
  name: string;
  detail_address: string;
  phone: string;
  dropoff_place: string;
  entry_method: string;
  dropoff_detail: string;
  entry_detail: string;
}

const DEFAULT_PERSONAL_DELIVERY_TYPE = '배송대행';
const DEFAULT_PERSONAL_SMS_TIMING = '7시전송';
const DEFAULT_ENTRY_METHOD = '공동현관 비밀번호';
const ENTRY_METHOD_REQUIRING_DETAIL = '공동현관 비밀번호';
const KLS_ENTRY_METHOD_OPTIONS = [
  '공동현관 비밀번호',
  '경비실 호출',
  '자유출입',
  '기타',
];

const emptyRecipient: RecipientDraft = {
  delivery_profile_id: '',
  profile_version: '',
  dong: '',
  ho: '',
  name: '',
  detail_address: '',
  phone: '',
  dropoff_place: '문 앞',
  entry_method: DEFAULT_ENTRY_METHOD,
  dropoff_detail: '',
  entry_detail: '',
};

const createDeliveryProfileId = (): string => {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256);
    });
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const listFrom = (value: any): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
};

const textToList = (value: string): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      if (seen.has(item)) return;
      seen.add(item);
      result.push(item);
    });
  return result;
};

const listToText = (items: any): string => listFrom(items).join('\n');

const legacyEntryDetailFrom = (recipient: any): string => {
  const explicitEntryDetail = String(recipient?.entry_detail || '').trim();
  if (explicitEntryDetail) return explicitEntryDetail;

  const entryMethod = String(recipient?.entry_method || '').trim();
  const legacyDetail = String(recipient?.dropoff_detail || '').trim();
  if (entryMethod === '공동현관 비밀번호' && legacyDetail) {
    return legacyDetail;
  }
  return '';
};

const normalizeRecipientEntryDraft = (recipient: any): Pick<RecipientDraft, 'entry_method' | 'entry_detail'> => {
  const rawEntryMethod = String(recipient?.entry_method || '').trim();
  const legacyEntryDetail = legacyEntryDetailFrom(recipient);
  if (KLS_ENTRY_METHOD_OPTIONS.includes(rawEntryMethod)) {
    return {
      entry_method: rawEntryMethod,
      entry_detail: legacyEntryDetail,
    };
  }
  if (rawEntryMethod) {
    return {
      entry_method: DEFAULT_ENTRY_METHOD,
      entry_detail: legacyEntryDetail || rawEntryMethod,
    };
  }
  return {
    entry_method: DEFAULT_ENTRY_METHOD,
    entry_detail: legacyEntryDetail,
  };
};

const collectApartmentNames = (data: ProgramData, serverConfigs: ApartmentConfigItem[]): string[] => {
  const names = new Set<string>();
  const dictKeys = [
    'line_settings',
    'apartment_ids',
    'apartment_addresses',
    'apartment_entry_passwords',
    'existing_customers',
    'cooler_bag_reissue',
    'flyer_recipients',
    'personal_delivery_configs',
  ];

  serverConfigs.forEach((item) => {
    const name = String(item.apartment_name || '').trim();
    if (name) names.add(name);
  });

  dictKeys.forEach((key) => {
    const section = data[key];
    if (!section || typeof section !== 'object' || Array.isArray(section)) return;
    Object.keys(section).forEach((name) => {
      const clean = String(name || '').trim();
      if (clean) names.add(clean);
    });
  });

  listFrom(data.apartment_order).forEach((name) => names.add(name));
  listFrom(data.personal_delivery_apartments).forEach((name) => names.add(name));

  const ordered: string[] = [];
  listFrom(data.apartment_order).forEach((name) => {
    if (names.has(name) && !ordered.includes(name)) ordered.push(name);
  });
  Array.from(names).sort().forEach((name) => {
    if (!ordered.includes(name)) ordered.push(name);
  });
  return ordered;
};

const recipientKeyFor = (recipient: RecipientDraft): string => {
  const dong = recipient.dong.trim() || '-';
  const ho = recipient.ho.trim() || '-';
  const name = recipient.name.trim();
  return `${dong}-${ho}-${name}`;
};

const summarizeConfigData = (data: ProgramData = {}) => {
  const personalConfigs = data.personal_delivery_configs || {};
  return {
    apartments: listFrom(data.apartment_order).length,
    personalApartments: listFrom(data.personal_delivery_apartments).length,
    personalConfigs: personalConfigs && typeof personalConfigs === 'object' ? Object.keys(personalConfigs).length : 0,
    existingCustomers: Object.values(data.existing_customers || {}).reduce((sum: number, list: any) => sum + listFrom(list).length, 0),
  };
};

const formatRevisionTime = (value?: string) => {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 19);
};

const ProgramConfigManager: React.FC = () => {
  const navigate = useNavigate();
  const apiBase = getApiBase();
  const token = localStorage.getItem('token');

  const [data, setData] = useState<ProgramData>({});
  const [serverConfigs, setServerConfigs] = useState<ApartmentConfigItem[]>([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [selectedRecipientKey, setSelectedRecipientKey] = useState('');
  const [recipientDraft, setRecipientDraft] = useState<RecipientDraft>(emptyRecipient);
  const [newApartmentName, setNewApartmentName] = useState('');
  const [rawJson, setRawJson] = useState('');
  const [showRawJson, setShowRawJson] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState<ConfigRevision[]>([]);
  const [addressKeyword, setAddressKeyword] = useState('');
  const [addressResults, setAddressResults] = useState<JusoSearchResult[]>([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressSearchMessage, setAddressSearchMessage] = useState('');
  const [addressSearchMessageType, setAddressSearchMessageType] = useState<'success' | 'error'>('success');
  const [updatedMeta, setUpdatedMeta] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const setStatus = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage(text);
    setMessageType(type);
  };

  const apartmentNames = useMemo(() => collectApartmentNames(data, serverConfigs), [data, serverConfigs]);
  const selectedServerConfig = useMemo(
    () => serverConfigs.find((item) => item.apartment_name === selectedApartment),
    [serverConfigs, selectedApartment]
  );

  const selectedConfig = useMemo(() => {
    const configs = data.personal_delivery_configs || {};
    return configs[selectedApartment] || {};
  }, [data, selectedApartment]);

  const selectedRecipients = useMemo(() => {
    const recipients = selectedConfig.recipients || {};
    return recipients && typeof recipients === 'object' ? recipients : {};
  }, [selectedConfig]);

  const selectedShared = useMemo(() => {
    const shared = selectedConfig.shared || {};
    return shared && typeof shared === 'object' ? shared : {};
  }, [selectedConfig]);

  const isPersonalDelivery = useMemo(
    () => listFrom(data.personal_delivery_apartments).includes(selectedApartment),
    [data.personal_delivery_apartments, selectedApartment]
  );
  const selectedApartmentCode = useMemo(
    () => String((data.apartment_ids || {})[selectedApartment] || selectedServerConfig?.apartment_id || ''),
    [data.apartment_ids, selectedApartment, selectedServerConfig]
  );

  const updateProgramData = (updater: (draft: ProgramData) => void) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      updater(next);
      setRawJson(JSON.stringify(next, null, 2));
      return next;
    });
  };

  const loadData = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const [programResp, apartmentsResp] = await Promise.all([
        fetch(`${apiBase}/api/admin/program-config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${apiBase}/api/admin/apartment-configs`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (programResp.status === 401 || apartmentsResp.status === 401) {
        navigate('/login');
        return;
      }
      if (!programResp.ok) throw new Error('apartdel 설정 조회 실패');
      if (!apartmentsResp.ok) throw new Error('아파트 설정 조회 실패');

      const programBody: ProgramConfigResponse = await programResp.json();
      const programData = programBody.data || {};
      const configs: ApartmentConfigItem[] = await apartmentsResp.json();

      setData(programData);
      setRawJson(JSON.stringify(programData, null, 2));
      setServerConfigs(configs);
      setUpdatedMeta(
        programBody.updated_at
          ? `마지막 저장: ${programBody.updated_at}${programBody.updated_by ? ` / ${programBody.updated_by}` : ''}`
          : '서버 저장 이력 없음'
      );

      const names = collectApartmentNames(programData, configs);
      if (!selectedApartment && names.length > 0) {
        setSelectedApartment(names[0]);
      }
      setStatus('apartdel 설정을 불러왔습니다.');
    } catch (err) {
      setStatus(normalizeFetchError(err, apiBase, 'apartdel 설정을 불러오지 못했습니다.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRevisions = async (showError = true) => {
    if (!token) return;
    setRevisionLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/program-config/revisions?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 401 || response.status === 403) {
        navigate('/login');
        return;
      }
      const body = await response.json().catch(() => []);
      if (!response.ok) throw new Error(body.detail || '변경 이력 조회 실패');
      setRevisions(Array.isArray(body) ? body : []);
      if (showError) setStatus('변경 이력을 새로 불러왔습니다.');
    } catch (err: any) {
      if (showError) {
        setStatus(err?.message || '변경 이력을 불러오지 못했습니다.', 'error');
      }
    } finally {
      setRevisionLoading(false);
    }
  };

  const restoreRevision = async (revision: ConfigRevision) => {
    if (!token) {
      navigate('/login');
      return;
    }
    const ok = window.confirm(
      `선택한 이력으로 apartdel 설정을 복원할까요?\n\n저장 시각: ${formatRevisionTime(revision.created_at)}\n메모: ${revision.note || '-'}`
    );
    if (!ok) return;

    setRestoringId(revision.id);
    try {
      const response = await fetch(`${apiBase}/api/admin/program-config/restore/${revision.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || '복원 실패');
      await loadData();
      await loadRevisions(false);
      setStatus(`이력 #${revision.id}로 복원했습니다. 복원 전 현재 설정도 이력/파일 백업으로 보관됩니다.`);
    } catch (err: any) {
      setStatus(err?.message || '이력 복원 중 오류가 발생했습니다.', 'error');
    } finally {
      setRestoringId(null);
    }
  };

  useEffect(() => {
    loadData();
    loadRevisions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedRecipientKey('');
    setRecipientDraft(emptyRecipient);
    setAddressKeyword('');
    setAddressResults([]);
    setAddressSearchMessage('');
  }, [selectedApartment]);

  const saveData = async (nextData: ProgramData = data, note = 'update apartdel config from admin web') => {
    if (!token) {
      navigate('/login');
      return false;
    }

    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/program-config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: nextData, note }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || '저장 실패');
      setUpdatedMeta(`마지막 저장: ${body.updated_at || ''}${body.updated_by ? ` / ${body.updated_by}` : ''}`);
      await loadRevisions(false);
      setStatus('apartdel 설정을 저장했습니다.');
      return true;
    } catch (err: any) {
      setStatus(err?.message || '저장 중 오류가 발생했습니다.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveRawJson = async () => {
    try {
      const parsed = JSON.parse(rawJson);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('JSON 최상위는 객체여야 합니다.');
      }
      setData(parsed);
      await saveData(parsed, 'save raw apartdel JSON from admin web');
    } catch (err: any) {
      setStatus(err?.message || 'JSON 형식이 올바르지 않습니다.', 'error');
    }
  };

  const searchAddress = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    const keyword = addressKeyword.trim();
    if (!keyword) {
      setStatus('주소 검색어를 입력하세요.', 'error');
      setAddressSearchMessage('주소 검색어를 입력하세요.');
      setAddressSearchMessageType('error');
      return;
    }

    setAddressSearching(true);
    setAddressSearchMessage('');
    try {
      const response = await fetch(`${apiBase}/api/admin/juso/search?keyword=${encodeURIComponent(keyword)}&count_per_page=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await response.json().catch(() => []);
      if (!response.ok) throw new Error(body.detail || '주소 검색 실패');
      const rows = Array.isArray(body) ? body : [];
      setAddressResults(rows);
      setAddressSearchMessage(rows.length > 0 ? `주소 ${rows.length}건을 찾았습니다. 아래 결과를 선택하세요.` : '검색 결과가 없습니다. 도로명과 건물번호만 입력해 다시 검색해보세요.');
      setAddressSearchMessageType(rows.length > 0 ? 'success' : 'error');
      setStatus(rows.length > 0 ? `주소 ${rows.length}건을 찾았습니다.` : '검색 결과가 없습니다.', rows.length > 0 ? 'success' : 'error');
    } catch (err: any) {
      setAddressResults([]);
      setAddressSearchMessage(err?.message || '주소 검색 중 오류가 발생했습니다.');
      setAddressSearchMessageType('error');
      setStatus(err?.message || '주소 검색 중 오류가 발생했습니다.', 'error');
    } finally {
      setAddressSearching(false);
    }
  };

  const applyAddressResult = (result: JusoSearchResult) => {
    const roadAddr = String(result.road_addr || '').trim();
    if (!roadAddr) return;
    setPersonalSharedValue('road_address', roadAddr);
    setPersonalSharedValue('postal_code', String(result.zip_no || '').trim());
    setAddressResults([]);
    setAddressKeyword(roadAddr);
    setStatus('도로명 주소를 적용했습니다. 최종 저장을 눌러 서버에 저장하세요.');
  };

  const importLocalJson = async (file: File | null) => {
    if (!file || !token) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('apartment_delivery_config.json 형식이 아닙니다.');
      }

      setSaving(true);
      const response = await fetch(`${apiBase}/api/admin/program-config/migrate-aprtdel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: parsed,
          note: `migrate apartdel JSON file: ${file.name}`,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || '이월 실패');

      setData(parsed);
      setRawJson(JSON.stringify(parsed, null, 2));
      await loadData();
      await loadRevisions(false);
      setStatus(`이월 완료: 아파트 ${body.apartment_config_upserted || 0}개, 설정 키 ${body.program_config_key_count || 0}개`);
    } catch (err: any) {
      setStatus(err?.message || '로컬 JSON 이월 중 오류가 발생했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apartdel_program_config_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setGlobalConfigValue = (sectionKey: string, field: string, value: string) => {
    updateProgramData((draft) => {
      const section = draft[sectionKey] && typeof draft[sectionKey] === 'object' && !Array.isArray(draft[sectionKey])
        ? draft[sectionKey]
        : {};
      section[field] = value;
      draft[sectionKey] = section;
    });
  };

  const setApartmentListSection = (sectionKey: string, text: string) => {
    updateProgramData((draft) => {
      const section = draft[sectionKey] && typeof draft[sectionKey] === 'object' && !Array.isArray(draft[sectionKey])
        ? draft[sectionKey]
        : {};
      const values = textToList(text);
      if (values.length > 0) {
        section[selectedApartment] = values;
      } else {
        delete section[selectedApartment];
      }
      draft[sectionKey] = section;
    });
  };

  const setApartmentTextMapValue = (sectionKey: string, value: string) => {
    updateProgramData((draft) => {
      const section = draft[sectionKey] && typeof draft[sectionKey] === 'object' && !Array.isArray(draft[sectionKey])
        ? draft[sectionKey]
        : {};
      const clean = value.trim();
      if (clean) {
        section[selectedApartment] = clean;
      } else {
        delete section[selectedApartment];
      }
      draft[sectionKey] = section;
    });
  };

  const togglePersonalDelivery = (checked: boolean) => {
    updateProgramData((draft) => {
      const current = listFrom(draft.personal_delivery_apartments);
      const next = checked
        ? Array.from(new Set([...current, selectedApartment])).sort()
        : current.filter((name) => name !== selectedApartment);
      draft.personal_delivery_apartments = next;

      if (checked) {
        const configs = draft.personal_delivery_configs && typeof draft.personal_delivery_configs === 'object'
          ? draft.personal_delivery_configs
          : {};
        configs[selectedApartment] = configs[selectedApartment] || {
          shared: {
            delivery_type: DEFAULT_PERSONAL_DELIVERY_TYPE,
            sms_timing: DEFAULT_PERSONAL_SMS_TIMING,
          },
          recipients: {},
          match_mode: 'dong_ho',
        };
        configs[selectedApartment].shared = {
          delivery_type: DEFAULT_PERSONAL_DELIVERY_TYPE,
          sms_timing: DEFAULT_PERSONAL_SMS_TIMING,
          ...(configs[selectedApartment].shared || {}),
        };
        draft.personal_delivery_configs = configs;
      }
    });
  };

  const setPersonalSharedValue = (field: string, value: string) => {
    updateProgramData((draft) => {
      const configs = draft.personal_delivery_configs && typeof draft.personal_delivery_configs === 'object'
        ? draft.personal_delivery_configs
        : {};
      const cfg = configs[selectedApartment] || { shared: {}, recipients: {}, match_mode: 'dong_ho' };
      cfg.shared = cfg.shared && typeof cfg.shared === 'object' ? cfg.shared : {};
      cfg.shared[field] = value;
      configs[selectedApartment] = cfg;
      draft.personal_delivery_configs = configs;
    });
  };

  const setPersonalMatchMode = (value: string) => {
    updateProgramData((draft) => {
      const configs = draft.personal_delivery_configs && typeof draft.personal_delivery_configs === 'object'
        ? draft.personal_delivery_configs
        : {};
      const cfg = configs[selectedApartment] || { shared: {}, recipients: {}, match_mode: 'dong_ho' };
      cfg.match_mode = value;
      configs[selectedApartment] = cfg;
      draft.personal_delivery_configs = configs;
    });
  };

  const loadRecipient = (key: string) => {
    const recipient = selectedRecipients[key] || {};
    const entryDraft = normalizeRecipientEntryDraft(recipient);
    setSelectedRecipientKey(key);
    setRecipientDraft({
      delivery_profile_id: String(recipient.delivery_profile_id || ''),
      profile_version: String(recipient.profile_version || ''),
      dong: String(recipient.dong || recipient.dong_raw || ''),
      ho: String(recipient.ho || ''),
      name: String(recipient.name || ''),
      detail_address: String(recipient.detail_address || ''),
      phone: String(recipient.phone || ''),
      dropoff_place: String(recipient.dropoff_place || '문 앞'),
      entry_method: entryDraft.entry_method,
      dropoff_detail: String(recipient.dropoff_detail || ''),
      entry_detail: entryDraft.entry_detail,
    });
  };

  const saveRecipient = () => {
    if (!selectedApartment) {
      setStatus('아파트를 먼저 선택하세요.', 'error');
      return;
    }
    if (!recipientDraft.name.trim()) {
      setStatus('수령자 이름은 필수입니다.', 'error');
      return;
    }
    const entryMethod = recipientDraft.entry_method.trim() || DEFAULT_ENTRY_METHOD;
    if (entryMethod === ENTRY_METHOD_REQUIRING_DETAIL && !recipientDraft.entry_detail.trim()) {
      setStatus('출입방법이 공동현관 비밀번호이면 출입방법 상세설명은 필수입니다.', 'error');
      return;
    }

    const newKey = recipientKeyFor(recipientDraft);
    const existingRecipient = selectedRecipientKey
      ? (selectedRecipients[selectedRecipientKey] || {})
      : {};
    const deliveryProfileId = (
      recipientDraft.delivery_profile_id.trim()
      || String(existingRecipient.delivery_profile_id || '').trim()
      || createDeliveryProfileId()
    );
    const profileVersion = new Date().toISOString();
    updateProgramData((draft) => {
      const configs = draft.personal_delivery_configs && typeof draft.personal_delivery_configs === 'object'
        ? draft.personal_delivery_configs
        : {};
      const cfg = configs[selectedApartment] || { shared: {}, recipients: {}, match_mode: 'dong_ho' };
      cfg.recipients = cfg.recipients && typeof cfg.recipients === 'object' ? cfg.recipients : {};
      if (selectedRecipientKey && selectedRecipientKey !== newKey) {
        delete cfg.recipients[selectedRecipientKey];
      }
      cfg.recipients[newKey] = {
        delivery_profile_id: deliveryProfileId,
        profile_version: profileVersion,
        dong: recipientDraft.dong.trim(),
        ho: recipientDraft.ho.trim(),
        name: recipientDraft.name.trim(),
        detail_address: recipientDraft.detail_address.trim(),
        phone: recipientDraft.phone.trim(),
        dropoff_place: recipientDraft.dropoff_place.trim(),
        entry_method: entryMethod,
        dropoff_detail: recipientDraft.dropoff_detail.trim(),
        entry_detail: recipientDraft.entry_detail.trim(),
      };
      configs[selectedApartment] = cfg;
      draft.personal_delivery_configs = configs;
      draft.personal_delivery_apartments = Array.from(new Set([...listFrom(draft.personal_delivery_apartments), selectedApartment])).sort();
    });
    setSelectedRecipientKey(newKey);
    setRecipientDraft((prev) => ({
      ...prev,
      delivery_profile_id: deliveryProfileId,
      profile_version: profileVersion,
    }));
    setStatus('수령자 정보를 반영했습니다. 최종 저장을 눌러 서버에 저장하세요.');
  };

  const removeRecipient = () => {
    if (!selectedRecipientKey) return;
    updateProgramData((draft) => {
      const configs = draft.personal_delivery_configs || {};
      const cfg = configs[selectedApartment] || {};
      if (cfg.recipients && typeof cfg.recipients === 'object') {
        delete cfg.recipients[selectedRecipientKey];
      }
      configs[selectedApartment] = cfg;
      draft.personal_delivery_configs = configs;
    });
    setSelectedRecipientKey('');
    setRecipientDraft(emptyRecipient);
  };

  const addApartment = () => {
    const name = newApartmentName.trim();
    if (!name) return;
    updateProgramData((draft) => {
      const order = listFrom(draft.apartment_order);
      if (!order.includes(name)) order.push(name);
      draft.apartment_order = order;
    });
    setSelectedApartment(name);
    setNewApartmentName('');
  };

  const counts = useMemo(() => {
    const personalConfigs = data.personal_delivery_configs || {};
    return {
      apartments: apartmentNames.length,
      personalApartments: listFrom(data.personal_delivery_apartments).length,
      personalConfigs: personalConfigs && typeof personalConfigs === 'object' ? Object.keys(personalConfigs).length : 0,
      existingCustomers: Object.values(data.existing_customers || {}).reduce((sum: number, list: any) => sum + listFrom(list).length, 0),
    };
  }, [data, apartmentNames.length]);

  if (loading) {
    return (
      <div className="program-config-page">
        <div className="pcm-loading">apartdel 설정을 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="program-config-page">
      <header className="pcm-header">
        <button className="pcm-back-btn" onClick={() => navigate('/admin')}>
          ← 대시보드
        </button>
        <div>
          <h1>apartdel 설정 이월 / 관리</h1>
          <p>{updatedMeta}</p>
        </div>
        <button className="pcm-save-btn" onClick={() => saveData()} disabled={saving}>
          {saving ? '저장 중...' : '서버 저장'}
        </button>
      </header>

      {message && (
        <div className={`pcm-message ${messageType === 'error' ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <section className="pcm-summary">
        <div><span>서버 등록 아파트</span><strong>{counts.apartments}</strong></div>
        <div><span>개인배송 아파트</span><strong>{counts.personalApartments}</strong></div>
        <div><span>TOMS 수령지 설정</span><strong>{counts.personalConfigs}</strong></div>
        <div><span>기존고객 동호수</span><strong>{counts.existingCustomers}</strong></div>
      </section>

      <section className="pcm-card pcm-import-card">
        <div>
          <h2>로컬 apartdel 정보 전체 이월</h2>
          <p>
            `apartment_delivery_config.json`을 선택하면 즉시 이월됩니다. 원본 설정 전체를 서버에 보관하고,
            아파트 ID/주소/공동현관/라인/순서도 서버 DB에 같이 반영합니다.
          </p>
          <p className="pcm-import-note">
            위 숫자는 현재 서버에 저장된 값 기준입니다. 이월 전에는 기존 테스트/일부 설정만 보일 수 있습니다.
          </p>
        </div>
        <div className="pcm-import-actions">
          <label className="pcm-file-btn">
            JSON 선택 후 바로 이월
            <input
              type="file"
              accept="application/json,.json"
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
              onChange={(e) => importLocalJson(e.target.files?.[0] || null)}
            />
          </label>
          <button className="pcm-secondary-btn" onClick={downloadJson}>서버 JSON 백업</button>
        </div>
      </section>

      <div className="pcm-content">
        <aside className="pcm-sidebar">
          <div className="pcm-card">
            <h2>아파트</h2>
            <div className="pcm-add-row">
              <input
                value={newApartmentName}
                onChange={(e) => setNewApartmentName(e.target.value)}
                placeholder="새 아파트명"
              />
              <button onClick={addApartment}>추가</button>
            </div>
            <div className="pcm-apartment-list">
              {apartmentNames.map((name) => (
                <button
                  key={name}
                  className={selectedApartment === name ? 'active' : ''}
                  onClick={() => setSelectedApartment(name)}
                >
                  <span>{name}</span>
                  {listFrom(data.personal_delivery_apartments).includes(name) && <em>개인</em>}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="pcm-main">
          <section className="pcm-card">
            <h2>전역 / TOMS 설정</h2>
            <div className="pcm-grid">
              <label>TOMS 아이디</label>
              <input
                value={String((data.kurly_upload_config || {}).username || '')}
                onChange={(e) => setGlobalConfigValue('kurly_upload_config', 'username', e.target.value)}
              />
              <label>TOMS 비밀번호</label>
              <input
                type="password"
                value={String((data.kurly_upload_config || {}).password || '')}
                onChange={(e) => setGlobalConfigValue('kurly_upload_config', 'password', e.target.value)}
              />
              <label>로그인 URL</label>
              <input
                value={String((data.kurly_upload_config || {}).login_url || '')}
                onChange={(e) => setGlobalConfigValue('kurly_upload_config', 'login_url', e.target.value)}
              />
              <label>주문등록 URL</label>
              <input
                value={String((data.kurly_upload_config || {}).order_add_url || '')}
                onChange={(e) => setGlobalConfigValue('kurly_upload_config', 'order_add_url', e.target.value)}
              />
            </div>
          </section>

          <section className="pcm-card">
            <div className="pcm-section-head">
              <h2>{selectedApartment || '아파트 선택'} 운영 설정</h2>
              <label className="pcm-toggle">
                <input
                  type="checkbox"
                  checked={isPersonalDelivery}
                  onChange={(e) => togglePersonalDelivery(e.target.checked)}
                  disabled={!selectedApartment}
                />
                개인배송 아파트
              </label>
            </div>

            <div className="pcm-grid pcm-operation-grid">
              <label>아파트 코드</label>
              <input
                value={selectedApartmentCode}
                onChange={(e) => setApartmentTextMapValue('apartment_ids', e.target.value)}
                placeholder="예: 123 또는 관리자 URL의 apt 값"
                disabled={!selectedApartment}
              />
              <div />
              <p className="pcm-inline-help">
                캐리힐 관리자 `내일의배송` URL에서 `apt=` 뒤에 붙는 값입니다. 이 코드가 있어야 서버 자동 차트 조회와 개인배송 TOMS 등록이 가능합니다.
              </p>
            </div>

            <div className="pcm-three-columns">
              <label>
                기존고객 동호수
                <textarea
                  value={listToText((data.existing_customers || {})[selectedApartment])}
                  onChange={(e) => setApartmentListSection('existing_customers', e.target.value)}
                  placeholder="예: 101-201"
                />
              </label>
              <label>
                보냉가방 재지급
                <textarea
                  value={listToText((data.cooler_bag_reissue || {})[selectedApartment])}
                  onChange={(e) => setApartmentListSection('cooler_bag_reissue', e.target.value)}
                  placeholder="예: 105-2302"
                />
              </label>
              <label>
                전단지 대상
                <textarea
                  value={listToText((data.flyer_recipients || {})[selectedApartment])}
                  onChange={(e) => setApartmentListSection('flyer_recipients', e.target.value)}
                  placeholder="예: 101-1203"
                />
              </label>
            </div>
          </section>

          <section className="pcm-card">
            <h2>개인배송 / TOMS 수령자 설정</h2>
            <div className="pcm-grid">
              <label>도로명 주소</label>
              <div className="pcm-address-field">
                <input
                  value={String(selectedShared.road_address || '')}
                  onChange={(e) => setPersonalSharedValue('road_address', e.target.value)}
                  disabled={!selectedApartment}
                />
                <div className="pcm-address-search">
                  <input
                    value={addressKeyword}
                    onChange={(e) => setAddressKeyword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchAddress();
                      }
                    }}
                    placeholder="예: 올림픽로 135, 잠실동 리센츠"
                    disabled={!selectedApartment || addressSearching}
                  />
                  <button
                    className="pcm-secondary-btn"
                    onClick={searchAddress}
                    disabled={!selectedApartment || addressSearching}
                  >
                    {addressSearching ? '검색 중...' : '주소검색'}
                  </button>
                </div>
                {addressResults.length > 0 && (
                  <div className="pcm-address-results">
                    {addressResults.map((item, index) => (
                      <button
                        key={`${item.road_addr}-${index}`}
                        onClick={() => applyAddressResult(item)}
                      >
                        <strong>{item.road_addr}</strong>
                        <span>{item.jibun_addr || item.building_name || item.zip_no}</span>
                      </button>
                    ))}
                  </div>
                )}
                {addressSearchMessage && (
                  <div className={`pcm-address-notice ${addressSearchMessageType === 'error' ? 'error' : 'success'}`}>
                    {addressSearchMessage}
                  </div>
                )}
              </div>
              <label>배송 유형</label>
              <input
                value={String(selectedShared.delivery_type || DEFAULT_PERSONAL_DELIVERY_TYPE)}
                onChange={(e) => setPersonalSharedValue('delivery_type', e.target.value)}
                placeholder="배송대행"
                disabled={!selectedApartment}
              />
              <label>우편번호</label>
              <input
                value={String(selectedShared.postal_code || '')}
                onChange={(e) => setPersonalSharedValue('postal_code', e.target.value)}
                placeholder="주소검색 시 자동 입력"
                disabled={!selectedApartment}
              />
              <label>문자 시점</label>
              <input
                value={String(selectedShared.sms_timing || DEFAULT_PERSONAL_SMS_TIMING)}
                onChange={(e) => setPersonalSharedValue('sms_timing', e.target.value)}
                placeholder="7시전송"
                disabled={!selectedApartment}
              />
              <label>동 매핑</label>
              <input
                value={String(selectedShared.dong_map_text || '')}
                onChange={(e) => setPersonalSharedValue('dong_map_text', e.target.value)}
                placeholder="A=101,B=102"
                disabled={!selectedApartment}
              />
              <label>매칭 기준</label>
              <select
                value={String(selectedConfig.match_mode || 'dong_ho')}
                onChange={(e) => setPersonalMatchMode(e.target.value)}
                disabled={!selectedApartment}
              >
                <option value="dong_ho">동/호</option>
                <option value="dong_ho_name">동/호/이름</option>
              </select>
            </div>

            <div className="pcm-recipient-layout">
              <div className="pcm-recipient-list">
                <div className="pcm-list-head">
                  <strong>수령자 {Object.keys(selectedRecipients).length}명</strong>
                  <button
                    onClick={() => {
                      setSelectedRecipientKey('');
                      setRecipientDraft(emptyRecipient);
                    }}
                  >
                    신규
                  </button>
                </div>
                {Object.keys(selectedRecipients).length === 0 ? (
                  <p className="pcm-muted">등록된 수령자가 없습니다.</p>
                ) : (
                  Object.entries(selectedRecipients).map(([key, recipient]: [string, any]) => (
                    <button
                      key={key}
                      className={selectedRecipientKey === key ? 'active' : ''}
                      onClick={() => loadRecipient(key)}
                    >
                      {recipient.name || key}
                      <span>{recipient.dong || '-'}-{recipient.ho || '-'}</span>
                    </button>
                  ))
                )}
              </div>

              <div className="pcm-recipient-form">
                <div className="pcm-grid compact">
                  <label>동</label>
                  <input value={recipientDraft.dong} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, dong: e.target.value }))} />
                  <label>호수</label>
                  <input value={recipientDraft.ho} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, ho: e.target.value }))} />
                  <label>이름 *</label>
                  <input value={recipientDraft.name} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, name: e.target.value }))} />
                  <label>연락처</label>
                  <input value={recipientDraft.phone} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, phone: e.target.value }))} />
                  <label>상세주소</label>
                  <input value={recipientDraft.detail_address} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, detail_address: e.target.value }))} />
                  <label>배송장소</label>
                  <input value={recipientDraft.dropoff_place} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, dropoff_place: e.target.value }))} />
                  <label>배송장소 상세설명</label>
                  <input value={recipientDraft.dropoff_detail} onChange={(e) => setRecipientDraft((prev) => ({ ...prev, dropoff_detail: e.target.value }))} />
                  <label>출입방법</label>
                  <select
                    value={recipientDraft.entry_method || DEFAULT_ENTRY_METHOD}
                    onChange={(e) => setRecipientDraft((prev) => ({ ...prev, entry_method: e.target.value }))}
                  >
                    {KLS_ENTRY_METHOD_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <label>출입방법 상세설명{(recipientDraft.entry_method || DEFAULT_ENTRY_METHOD) === ENTRY_METHOD_REQUIRING_DETAIL ? ' *' : ''}</label>
                  <input
                    value={recipientDraft.entry_detail}
                    onChange={(e) => setRecipientDraft((prev) => ({ ...prev, entry_detail: e.target.value }))}
                    placeholder={(recipientDraft.entry_method || DEFAULT_ENTRY_METHOD) === ENTRY_METHOD_REQUIRING_DETAIL ? '예: 종모양111, #1234' : ''}
                    required={(recipientDraft.entry_method || DEFAULT_ENTRY_METHOD) === ENTRY_METHOD_REQUIRING_DETAIL}
                  />
                </div>
                <div className="pcm-actions">
                  <button className="pcm-save-btn" onClick={saveRecipient}>수령자 반영</button>
                  <button className="pcm-danger-btn" onClick={removeRecipient} disabled={!selectedRecipientKey}>수령자 삭제</button>
                </div>
              </div>
            </div>
          </section>

          <section className="pcm-card">
            <div className="pcm-section-head">
              <div>
                <h2>변경 이력 / 복원</h2>
                <p className="pcm-help">
                  서버 저장, JSON 이월, 복원 전에 이전 설정이 자동으로 남습니다. 잘못 저장했을 때 여기서 되돌릴 수 있습니다.
                </p>
              </div>
              <div className="pcm-actions compact-actions">
                <button className="pcm-secondary-btn" onClick={() => loadRevisions()} disabled={revisionLoading}>
                  {revisionLoading ? '조회 중...' : '이력 새로고침'}
                </button>
                <button className="pcm-secondary-btn" onClick={() => setShowRevisions((prev) => !prev)}>
                  {showRevisions ? '이력 닫기' : '이력 보기'}
                </button>
              </div>
            </div>

            {showRevisions && (
              <div className="pcm-revision-list">
                {revisions.length === 0 ? (
                  <p className="pcm-muted">아직 저장 이력이 없습니다.</p>
                ) : (
                  revisions.map((revision) => {
                    const summary = summarizeConfigData(revision.data || {});
                    return (
                      <div className="pcm-revision-row" key={revision.id}>
                        <div>
                          <strong>#{revision.id} {formatRevisionTime(revision.created_at)}</strong>
                          <p>{revision.note || '메모 없음'} / {revision.created_by || '-'}</p>
                          <span>
                            아파트 {summary.apartments}개 · 개인배송 {summary.personalApartments}개 · TOMS 설정 {summary.personalConfigs}개 · 기존고객 {summary.existingCustomers}건
                          </span>
                        </div>
                        <button
                          className="pcm-danger-btn"
                          onClick={() => restoreRevision(revision)}
                          disabled={restoringId === revision.id}
                        >
                          {restoringId === revision.id ? '복원 중...' : '이 버전 복원'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </section>

          <section className="pcm-card">
            <div className="pcm-section-head">
              <h2>원본 JSON 안전 편집</h2>
              <button className="pcm-secondary-btn" onClick={() => setShowRawJson((prev) => !prev)}>
                {showRawJson ? '닫기' : '열기'}
              </button>
            </div>
            {showRawJson && (
              <>
                <p className="pcm-help">
                  화면에 없는 `scheduled_additions`, `first_purchase_by_date`, API 키, 로컬 경로 등도 여기에서 그대로 보존됩니다.
                </p>
                <textarea
                  className="pcm-raw-json"
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  spellCheck={false}
                />
                <div className="pcm-actions">
                  <button className="pcm-save-btn" onClick={saveRawJson} disabled={saving}>원본 JSON 저장</button>
                </div>
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default ProgramConfigManager;
